/**
 * useMoveGalleryPhoto — office "Wrong unit: move this photo" (callable fieldService_galleryMove).
 *
 * Optimistic: the photo leaves THIS unit's gallery and "All photos" lists at once, comes back on error,
 * and everything is revalidated on settle (this unit's gallery count / cover changed on the server too).
 * On success the unit it moved TO is revalidated as well, so opening that unit shows the photo.
 * The server decides everything that matters (office only, people only, the target unit resolved from
 * the Generator ID); its plain-language refusal is shown as the toast.
 */
import { useMutation } from '@tanstack/react-query';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import toast from 'react-hot-toast';
import { functions } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { generatorKeys } from '../useGenerator';
import type { GeneratorPhoto } from '../../types/fieldService/generatorPhotos';
import { galleryErrorMessage, generatorPhotoKeys, useGalleryCache } from './useGeneratorGallery';

export interface MoveGalleryPhotoInput {
  photoId: string;
  fromGeneratorId: string;
  toGeneratorId: string;
}

export interface MoveGalleryPhotoResult {
  changed: boolean;
  toGeneratorDocId: string;
  /** The old unit's location pin was worked out from this photo: the office should review that pin. */
  oldUnitPinCitedPhoto?: boolean;
}

/** How long the "review the old unit's pin" notice stays up: it asks for an action, so longer than a plain success. */
export const PIN_REVIEW_NOTICE_MS = 12000;

const moveFn = httpsCallable<MoveGalleryPhotoInput & { organizationId: string }, MoveGalleryPhotoResult>(
  functions,
  'fieldService_galleryMove'
);

interface LibraryPages {
  pages: Array<{ rows: GeneratorPhoto[] }>;
}

export function useMoveGalleryPhoto(generatorDocId: string) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const cache = useGalleryCache(orgId, generatorDocId);

  return useMutation({
    mutationFn: async (input: MoveGalleryPhotoInput) => (await moveFn({ organizationId: orgId, ...input })).data,
    onMutate: async ({ photoId }) => {
      const snapshot = await cache.snapshot();
      const stays = (row: { id: string }) => row.id !== photoId;
      cache.queryClient.setQueryData<GeneratorPhoto[]>(cache.galleryKey, (old) => old?.filter(stays));
      cache.queryClient.setQueriesData<LibraryPages>(
        { queryKey: generatorPhotoKeys.libraries(orgId, generatorDocId) },
        (old) => (old ? { ...old, pages: old.pages.map((p) => ({ ...p, rows: p.rows.filter(stays) })) } : old)
      );
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      cache.restore(ctx?.snapshot);
      toast.error(galleryErrorMessage(err, 'Could not move the photo to the other unit.'));
    },
    onSuccess: (result, { fromGeneratorId, toGeneratorId }) => {
      // changed:false = a retry of a move that had already landed: say that, do not claim a second move.
      toast.success(result.changed ? `Photo moved to ${toGeneratorId}. It is under "All photos" there.` : `This photo was already on ${toGeneratorId}.`);
      if (result.oldUnitPinCitedPhoto) {
        toast(`Review the location pin on ${fromGeneratorId}: it was worked out from this photo.`, { duration: PIN_REVIEW_NOTICE_MS });
      }
      const qc = cache.queryClient;
      void qc.invalidateQueries({ queryKey: generatorPhotoKeys.gallery(orgId, result.toGeneratorDocId) });
      void qc.invalidateQueries({ queryKey: generatorPhotoKeys.libraries(orgId, result.toGeneratorDocId) });
      void qc.invalidateQueries({ queryKey: generatorKeys.detail(result.toGeneratorDocId) });
      // Report pickers cache these rows by report, and each row carries its unit.
      void qc.invalidateQueries({ queryKey: generatorPhotoKeys.submissions() });
    },
    onSettled: () => cache.invalidateAll(),
  });
}
